<template>
  <modal id="qb-save-query" @closeModal="onCancel" containerClass="modal-lg">
    <template slot="header">
      <h3>{{$t('save.title')}}</h3>
    </template>
    <template slot="body">
      <ul v-if="isValidationErrors" class="error-msgs">
        <li v-for="(message, i) in validationMessages" :key="i">{{message}}</li>
      </ul>
      <load-content :loading="queryData.isBusy" class="content">
        <div class="flex-1to1">
          <div>
            <div class="d-flex justify-content-between">
              <h5>{{$t('save.choose_folder')}}</h5>
              <span class="new-folder fas fa-folder-plus"
                    v-tooltip="$t('save.new_folder')"
                    v-on:click="() => toggleNewFolder()"
              ></span>
            </div>

            <form-renderer v-if="isNewFolderOpen"
                           class="light-border clearfix mb10 "
                           id="new-folder-form"
                           :fields="['label']"
                           :fieldsAdditions="{label: { unique: { values: parentChildrenFoldersLabels}}}"
                           :showMessageNotifications="false"
                           @onSave="createNewFolder"
                           @onCancel="cancelAddFolder"
              />

            <load-content :loading="folderStructure.isBusy" class="folder-structure-wrapper position-relative w-100">
              <folder-structure v-if="selectedFolder"
                :data="folderStructure.data || []"
                :location="selectedFolder.id"
                @update="(value) => onChange('folder_id', value)"
                class="flex-1 light-border clearfix"
                :class="{'has-error': folderHasError}"
              />
            </load-content>
          </div>
          <div class="flex-2">
            <div class="form-group">
              <label for="name">{{$t('save.query_name')}}</label>
              <input-text
                id="name"
                :value="newQuery.name"
                :onChange="(value) => onChange('name', value)"
                :class="{'has-error': nameHasError}"
              />
            </div>

            <div class="form-group">
              <label for="target">{{$t('save.target')}}</label>
              <div class="checkboxes">
                <label class="checkboxes">
                  <input-checkbox
                    :value="newQuery.target.includes('reporting')"
                    :onChange="(value) => onChangeTarget('reporting', value)"
                  /> {{$t('save.reporting')}}
                </label>
                <label class="checkboxes">
                  <input-checkbox
                    :value="newQuery.target.includes('portal')"
                    :onChange="(value) => onChangeTarget('portal', value)"
                  /> {{$t('save.portal')}}
                </label>
              </div>
            </div>

            <div class="form-group">
              <div>
                <label class="checkboxes">
                  <input-checkbox
                    :value="!newQuery.is_private"
                    :onChange="(value) => onChange('is_private', value)"
                  /> {{$t('save.share')}}
                </label>
              </div>
            </div>

            <div v-if="projectOptions.length > 0" class="form-group">
              <label for="project">{{$t('save.project')}}</label>
              <input-select class="project-dropdown" :options="projectOptions" :selectedValue="projectId" :allowEmpty="false" :onChange="(value) => onChange('project_id', value)" />
            </div>

          </div>
        </div>
      </load-content>
    </template>
    <template slot="footer">
      <div class="align-right">
        <button class="btn btn-sm btn-default" @click="onCancel">{{$t('save.actions.cancel')}}</button>
        <button class="btn btn-sm btn-carma" @click="onSaveAs" :disabled="folderStructure.isBusy || queryData.isBusy">{{$t('save.actions.save')}}</button>
      </div>
    </template>
  </modal>
</template>

<script>
  import { mapState, mapActions } from 'vuex';
  import _ from 'lodash';

  import Modal from '../modal';
  import FormRenderer from '../form-renderer';
  import LoadContent from '../load-content';
  import FolderStructure from './folder_structure';
  import InputText from '../form-components/input-text';
  import InputCheckbox from '../form-components/input-checkbox';
  import InputSelect from '@/components/form-components/input-select';

  export default {
    props: {
      query: {
        type: Object,
        required: () => true
      },
      isEdit: {
        type: Boolean,
        default: false
      }
    },
    components: {
      'modal': Modal,
      'load-content': LoadContent,
      'folder-structure': FolderStructure,
      'input-text': InputText,
      'input-checkbox': InputCheckbox,
      FormRenderer,
      'input-select': InputSelect
    },
    computed: {
      ...mapState({
        queryData: state => state.queryBuilder.query.query,
        queries: state => state.queries.queries,
        folderStructure: state => state.queryBuilder.folderStructure.folderStructure,
        project: state => state.projects.project.data,
      }),
      projectId() {
        return this.newQuery.project_id || this.$route.params.project_id;
      },
      selectedFolder () {
        const data = this.folderStructure.data;
        if (!data) return;

        if (this.newQuery.folder_id) {
          return data.find(folder => folder.id === this.newQuery.folder_id);
        }

        return data.find(folder => !folder.parent);
      },
      foldersData() { return this.folderStructure.data ? this.folderStructure.data : [] },
      parentChildrenFolders() { return this.foldersData.filter(folder => folder.parent === this.selectedFolder.id)},
      parentChildrenFoldersLabels() { return this.parentChildrenFolders.map((folder) => folder.label)},

      projectOptions () {
        if (this.project?.child_projects?.length) {
          const options = this.project.child_projects.map(p => ({ value: p.id, label: p.name }));
          return [{ value: this.project.project_id, label: this.project.project_name }, ...options];
        }
        return [];
      }
    },
    created () {
      this.newQuery = _.cloneDeep(this.query);
      if(!this.isEdit){
        this.newQuery.sourceQueryId = this.newQuery.id;
        delete this.newQuery.id;
      } 

      const queryProjectId = this.$route.query.project_id;
      if (queryProjectId && this.projectOptions.find(option => option.value == queryProjectId)) {
        this.newQuery.project_id = queryProjectId;
      }

      this.loadFolderStructure();
    },
    watch: {
      selectedFolder : {
        handler (newValue, oldValue) {
          if(newValue && newValue !== oldValue) {
            this.loadSelectedFolderQueries();
          }
        }
      }
    },
    methods: {
      ...mapActions({
        'getQueries': 'queries/getQueries',
        'getFolderStructure': 'queryBuilder/folderStructure/getFolderStructure',
        'addFolder': 'queryBuilder/folderStructure/addFolder'
      }),
      loadFolderStructure () {
        const { projectId } = this;
        this.getFolderStructure({ projectId: projectId });
      },
      loadSelectedFolderQueries () {
        this.getQueries({ projectId: this.projectId, folderId: this.selectedFolder.id });
      },

      onCancel () {
        this.$emit('cancel');
      },

      onSaveAs () {
        const { projectId, newQuery } = this;
        if(_.isUndefined(newQuery.folder_id) || _.isEmpty(newQuery.folder_id)) {
          newQuery.folder_id = this.selectedFolder.id;
        }

        this.clearAllErrors();
        this.validationMessages = this.validateQuery(newQuery);

        if(this.validationMessages.length === 0) {
          this.$emit('save', { projectId, query: newQuery });
        } else {
          this.isValidationErrors = true;
        }
      },

      clearAllErrors () {
        this.validationMessages = [];
        this.nameHasError = false;
      },

      validateQuery (query) {
        let errors = [];

        if(!query.name || query.name.trim() === '') {
          errors.push(this.$i18n.t('form.validation_messages.empty_field_error', [this.$i18n.t('save.query_name')]));
          this.nameHasError = true;
        }
        if(_.isUndefined(query.folder_id) || query.folder_id === '' || !/^\d+$/.test(query.folder_id)) {
          errors.push(this.$i18n.t('form.validation_messages.select_folder_error'));
          this.folderHasError = true;
        }

        if(this.queries.data && this.queries.data.data && this.queries.data.data.data.length > 0) {
          const nameExists = this.queries.data.data.data.find(q => q.name && query.name && q.name.trim().toLowerCase() === query.name.trim().toLowerCase());

          if(nameExists && (!this.isEdit || nameExists.id !== query.id)) {
            errors.push(this.$i18n.t('form.validation_messages.entry_exists', [this.$i18n.t('save.query_name')]));
            this.nameHasError = true;
          }
        }

        return errors;
      },

      onChange (type, value) {
        // On clear errors avoid rerender
        this.isValidationErrors = false;

        if (type === 'is_private') {
          value = !value;
        }
        this.$set(this.newQuery, type, value);

        if (type === 'project_id') {
          this.newQuery.folder_id = null;
          this.getFolderStructure({ projectId: value });
        }
      },

      onChangeTarget (type, value) {
        let { target } = this.newQuery;
        value === true ? !target.find(t => t === type) && this.onChange('target', [...target, type]) : this.onChange('target', target.filter(t => t !== type));
      },

      toggleNewFolder (isNewFolderOpen) {
        if(_.isUndefined(isNewFolderOpen)) {
          isNewFolderOpen = !this.isNewFolderOpen;
        }

        this.newFolderModel = {};
        this.isNewFolderOpen = isNewFolderOpen;
      },

      updateNewFolder (model) {
        this.newFolderModel = model;
      },

      createNewFolder (newFolderModel) {
        this.addFolder({
          projectId: this.projectId,
          model: { ...newFolderModel, parent: this.selectedFolder.id }
        });

        this.toggleNewFolder(false);
      },

      cancelAddFolder () {
        this.toggleNewFolder(false);
      }
    },
    data () {
      return {
        newQuery: {},
        validationMessages: [],
        isValidationErrors: false,
        nameHasError: false,
        folderHasError: false,
        isNewFolderOpen: false
      }
    }
  }
</script>

<style lang="scss">
  @import "../../styles/components/coding-queries/save-query-modal";
</style>